import { toDataURL } from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  AuthError,
  answerTotp,
  completeNewPassword,
  confirmTotpSetup,
  federatedSignInUrl,
  googleSignInUrl,
  otpauthUrl,
  signIn,
  type SignInStep,
} from '../../auth/cognito.js';
import { config } from '../../config.js';
import { useSession } from '../../session/SessionProvider.js';
import { Button, Field, InlineError, InlineInfo, TextInput } from '../../ui/index.js';
import { AuthLayout, AuthTitle } from './AuthLayout.js';
import { CodeInput } from './CodeInput.js';
import styles from './Auth.module.css';

type Step =
  | { kind: 'credentials' }
  | { kind: 'newPassword'; step: Extract<SignInStep, { kind: 'newPassword' }> }
  | { kind: 'mfaSetup'; step: Extract<SignInStep, { kind: 'mfaSetup' }>; qr: string | null }
  | { kind: 'totp'; step: Extract<SignInStep, { kind: 'totpRequired' }> }
  | { kind: 'locked' };

const DEV_ACTORS = [
  { value: 'staff:franco@dinocloud.com', label: 'Franco Salonia · DinoCloud Platform Owner' },
  { value: 'tenant:andres.colombo@bancodelta.example', label: 'Andrés Colombo · Banco Delta superadmin' },
  { value: 'tenant:ana.ruiz@bancodelta.example', label: 'Ana Ruiz · Banco Delta operator' },
  { value: 'tenant:carla.mendez@nortec.example', label: 'Carla Méndez · Nortec Logistics superadmin' },
];

/** Spec 01: sign in, new password, second factor set-up, code entry, locked. */
export function SignInFlow() {
  const { signInWithTokens, signInDev, isDev, refusal } = useSession();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failures, setFailures] = useState(0);
  const [sso, setSso] = useState<{ providerName: string; label: string; enforce: boolean } | null>(null);

  const after = location.state?.from && location.state.from !== '/auth/sign-in' ? location.state.from : '/';

  // Email first: when the address belongs to an organization with single sign-on, offer it
  // and hide the password when the organization requires it.
  useEffect(() => {
    const address = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address)) {
      setSso(null);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(() => {
      fetch(`${config.apiUrl}/auth/sso?email=${encodeURIComponent(address)}`, { signal: controller.signal })
        .then((r) => (r.ok ? r.json() : { sso: null }))
        .then((body: { sso: { providerName: string; label: string; enforce: boolean } | null }) => setSso(body.sso))
        .catch(() => setSso(null));
    }, 350);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [email]);

  const onSso = () => void handle(async () => {
    if (sso) window.location.assign(await federatedSignInUrl(sso.providerName));
  });

  const finish = async (tokens: Awaited<ReturnType<typeof answerTotp>>) => {
    await signInWithTokens(tokens);
    navigate(after, { replace: true });
  };

  const applyStep = async (s: SignInStep) => {
    setCode('');
    setError(null);
    switch (s.kind) {
      case 'done':
        await finish(s.tokens);
        return;
      case 'newPassword':
        setStep({ kind: 'newPassword', step: s });
        return;
      case 'totpRequired':
        setStep({ kind: 'totp', step: s });
        return;
      case 'mfaSetup': {
        setStep({ kind: 'mfaSetup', step: s, qr: null });
        const qr = await toDataURL(otpauthUrl(s.secret, email.trim().toLowerCase()), { margin: 1, width: 160 });
        setStep({ kind: 'mfaSetup', step: s, qr });
        return;
      }
    }
  };

  const handle = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      if (err instanceof AuthError && err.code === 'locked') {
        setStep({ kind: 'locked' });
      } else if (err instanceof AuthError && err.code === 'invalid') {
        const n = failures + 1;
        setFailures(n);
        setError(err.message);
      } else if (err instanceof AuthError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const onCredentials = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    void handle(async () => applyStep(await signIn(email, password)));
  };

  const onGoogle = () => void handle(async () => {
    window.location.assign(await googleSignInUrl());
  });

  useEffect(() => {
    if (step.kind === 'totp' && code.length === 6 && !busy) {
      void handle(async () => {
        try {
          await finish(await answerTotp(step.step.user, code));
        } catch (err) {
          setCode('');
          if (err instanceof AuthError && err.code === 'code' && failures + 1 >= 3) {
            setStep({ kind: 'locked' });
            return;
          }
          setFailures((n) => n + 1);
          throw err;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step.kind]);

  if (step.kind === 'locked') {
    return (
      <AuthLayout>
        <div className={styles.form}>
          <AuthTitle
            title="Your account is locked"
            description="Too many failed attempts. Access is restored by your organization’s administrator, or by your DinoCloud CSM if you are the superadmin. There is no retry here."
          />
          <Button onClick={() => window.location.assign('mailto:support@dinocloud.com?subject=Titan%20access')}>
            Contact support
          </Button>
          <div className={styles.links}>
            <Link to="/auth/sign-in" onClick={() => setStep({ kind: 'credentials' })}>
              Back to sign in
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (step.kind === 'newPassword') {
    return (
      <AuthLayout>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void handle(async () => applyStep(await completeNewPassword(step.step.user, newPassword)));
          }}
        >
          <AuthTitle title="Set your password" description="Your invitation came with a temporary password. Choose your own to continue." />
          {error && <InlineError>{error}</InlineError>}
          <Field label="New password" hint="At least 12 characters, with upper and lower case, a number and a symbol." htmlFor="new-password">
            <TextInput id="new-password" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          </Field>
          <Button type="submit" size="lg" full disabled={busy || newPassword.length < 12}>
            Continue
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (step.kind === 'mfaSetup') {
    return (
      <AuthLayout>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void handle(async () => finish(await confirmTotpSetup(step.step.user, code)));
          }}
        >
          <AuthTitle
            title="Set up your second factor"
            description="Every sign-in to Titan uses a code from an authenticator app. Scan the code with Google Authenticator, 1Password or any TOTP app, then enter the six digits it shows."
          />
          {error && <InlineError>{error}</InlineError>}
          <div className={styles.qr}>
            {step.qr ? <img className={styles.qrImage} src={step.qr} alt="QR code for your authenticator app" /> : <div className={styles.qrImage} />}
            <div>
              <div className={styles.staffNote}>Or enter this key manually</div>
              <div className={styles.secret}>{step.step.secret}</div>
            </div>
          </div>
          <CodeInput value={code} onChange={setCode} disabled={busy} />
          <Button type="submit" size="lg" full disabled={busy || code.length !== 6}>
            Confirm and sign in
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (step.kind === 'totp') {
    return (
      <AuthLayout>
        <div className={styles.form}>
          <AuthTitle title="Enter your verification code" description="Open your authenticator app and type the six digits for Titan." />
          {error && <InlineError>{error}</InlineError>}
          <CodeInput value={code} onChange={setCode} disabled={busy} />
          <div className={styles.links}>
            <Link to="/auth/sign-in" onClick={() => setStep({ kind: 'credentials' })}>
              Use a different account
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form className={styles.form} onSubmit={onCredentials}>
        <AuthTitle title="Sign in" description="Use the email your organization registered for Titan." />
        {refusal && !error && <InlineError>{refusal}</InlineError>}
        {error && <InlineError>{error}</InlineError>}
        <Field label="Email" htmlFor="email">
          <TextInput id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        {sso && (
          <>
            <Button size="lg" full onClick={onSso} disabled={busy}>
              Continue with {sso.label}
            </Button>
            <p className={styles.staffNote}>
              {sso.enforce ? 'Your organization signs in with single sign-on. There is no separate password.' : 'Your organization offers single sign-on. You can also use your password below.'}
            </p>
          </>
        )}
        {!(sso && sso.enforce) && (
          <>
            <Field label="Password" htmlFor="password">
              <TextInput id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required={!sso} />
            </Field>
            <Button type="submit" size="lg" full variant={sso ? 'secondary' : 'primary'} disabled={busy || !email.trim() || !password}>
              Sign in
            </Button>
            <div className={styles.links}>
              <Link to="/auth/forgot">Forgot your password?</Link>
            </div>
          </>
        )}
        <div className={styles.divider}>DinoCloud staff</div>
        <Button variant="secondary" size="lg" full onClick={onGoogle} disabled={busy}>
          Continue with Google
        </Button>
        <p className={styles.staffNote}>Staff sign in with their DinoCloud Google account. Client accounts use email and password.</p>
        {isDev && (
          <>
            <div className={styles.divider}>Local development</div>
            <InlineInfo>
              <div className={styles.devPicker}>
                {DEV_ACTORS.map((a) => (
                  <Button key={a.value} variant="outline" size="sm" onClick={() => void signInDev(a.value).then(() => navigate(after, { replace: true }))}>
                    {a.label}
                  </Button>
                ))}
              </div>
            </InlineInfo>
          </>
        )}
      </form>
    </AuthLayout>
  );
}
